/**
 * @fileoverview Which event kinds Pages reads and writes, and why it still
 * reads one that is somebody else's.
 *
 * EVERY PAGE WE PUBLISH IS OUR OWN KIND (33301) AND ITS BODY IS ALWAYS A BLOB.
 * Operator ruling 2026-09-16, reversing the ruling of the day before. A page
 * carries a structural vocabulary and a theme, so publishing one as long-form
 * publishes a degraded copy under the author's own name: directives render as
 * literal text and the theme is ignored.
 *
 * THE LONG-FORM KIND STAYS IN THIS FILE ON PURPOSE, as a READER capability and
 * nothing we write. `RENDERABLE_PAGE_KINDS` names it so a stranger's long-form
 * article opens here, and the slug check and the readable-URL resolver query it
 * because an author's own articles written in another client live in the same
 * readable namespace. Rendering them costs nothing. Publishing one does not
 * happen.
 *
 * EVERYTHING FROM HERE TO "OUR OWN KIND" IS THE 2026-09-15 REASONING AND IS
 * KEPT BECAUSE IT WAS SOUND. What changed is the product, not the analysis, and
 * its measurements are still measurements. Read the sentences declaring what a
 * public markdown page IS as history: they were true for one day.
 *
 * A PUBLIC MARKDOWN PAGE IS AN ORDINARY NIP-23 LONG-FORM EVENT (30023) WITH THE
 * MARKDOWN INLINE. No Blossom blob, no pointer, no kind of ours. Settled by the
 * operator on 2026-09-15, reversing a ruling made hours earlier the same day.
 *
 * The reversal is worth keeping because the mistake was in the FRAMING. The
 * earlier ruling asked "our own kind, or 30023?" and answered it correctly
 * inside that frame: a 30023 whose `.content` holds a blob POINTER renders as a
 * blank article in a long-form reader, and blank in someone else's client is
 * worse than absent -- absent is nothing, blank is a bug report against us.
 * (Not "every" long-form reader, because one of the seven we measured would
 * never have fetched it at all. See the Alexandria paragraph below. The
 * universal form does not belong in this file even in a sentence about a shape
 * we rejected.)
 * The question one level up never got asked: should a public markdown page's
 * body be in the event at all, or in Blossom? "Storage is
 * Blossom, the index is a Nostr event" was inherited from the recovered design
 * and treated as settled for all three cases. It is only NECESSARY for the two
 * that 30023 cannot express. For public markdown it was a free choice, and it
 * was the one that cut us off from the entire existing long-form readership.
 *
 * Verified against the spec rather than memory: NIP-23 says ".content of these
 * events should be a string text in Markdown syntax". It does not permit a
 * pointer. So the body goes in the event, and SIX OF THE SEVEN long-form
 * readers we measured render our pages on day one with no work from us. Six of
 * seven, deliberately not "every": the seventh is named in the paragraph after
 * next and the universal form of this sentence is false.
 *
 * WHICH READERS, MEASURED RATHER THAN LISTED FROM MEMORY. A page we published
 * was opened in six live clients on 2026-09-15 and rendered in full by all six,
 * headings and structure intact: njump, Yakihonne, Highlighter, Primal,
 * noStrudel and Coracle. Every one of them found a page that exists only on
 * `relay.cloistr.xyz`, a relay in none of their default lists, purely from the
 * hint packed into the address. Habla and Blogstack, which our earlier notes
 * cited as the obvious readers, are both DEAD deployments. Damus and Amethyst
 * are mobile and were not measured; no claim is made about them here.
 *
 * THE SEVENTH CLIENT IS THE EXCEPTION AND IT IS A REAL ONE. Alexandria was
 * measured on a second pass the same day and does not use the relay hint at
 * all. It answers "Event not found" for our page, answers identically for a
 * stranger's article on another relay it does not already carry, and renders a
 * plain 30023 in full through that same route when the event is on one of its
 * own relays. So the kind is supported and the harness works: the hint is the
 * thing it ignores. "Any long-form client renders our pages" is therefore false
 * as an absolute. Six of seven, and the seventh shows a reader a not-found for
 * a page that exists, with nothing to say their client simply did not look.
 *
 * NOTHING IN OUR EVENT CAN FIX THAT, and this is worth saying before someone
 * goes looking. Alexandria queries only relays it already carries, so the
 * address can name our relay as loudly as it likes and be ignored. The general
 * answer is the outbox model, not anything a publisher of ours emits. Ruled
 * 2026-09-15: correct the claim, change nothing in the product. Publishing
 * users' pages to third-party relays to route around one client would put their
 * content on infrastructure we do not run, and if we ever want it, it belongs
 * to the AUTHOR as a choice and never as a silent default.
 *
 * AND IT IS THE SAME FACT ON THE PRIVATE PATH, not a second one. Private-page
 * discovery is a relay query for the recipient's own share events, so a client
 * that will not leave its own relay set cannot participate in that either.
 * Alexandria is simultaneously the likeliest place a decryption convention
 * would ever be adopted, being the one client built on curated multi-event
 * publications, and the client least able to see our pages today. Those point
 * in opposite directions and both are worth holding.
 *
 * Our theme reference and layout hints ride on the 30023 as extra tags. Clients
 * that do not know them ignore them and show plain prose, which is the
 * graceful-degradation contract honoured rather than invoked -- and all six
 * dropped our `toc` and `width` tags with ZERO stray text leaking into their
 * renders, so that contract is now proved from the other side too.
 * (NO LONGER TRUE OF OUR PAGES after 2026-09-16. Those tags ride on our own
 * kind now, which none of the six render at all. The measurement stands as a
 * fact about 30023 and about the contract; it is not a fact about us.)
 *
 * AND THE SAME RUN MEASURED WHAT AN ENCRYPTED PAGE LOOKS LIKE TO A STRANGER. A
 * 30023 whose body is NIP-44 ciphertext was found by all six clients and
 * decrypted by none, and every one printed six hundred characters of base64
 * under the title as though that were the article. Reusing 30023 for a PRIVATE
 * page is therefore the worst outcome available, and that much is measured.
 *
 * WHAT DOES NOT FOLLOW FROM IT, AND THIS COMMENT USED TO CLAIM IT DID. The
 * second pass measured a 33301 with the ciphertext actually INLINE, and four of
 * the same six dumped it exactly as readily as they dumped it on 30023. Only
 * njump degrades honestly and only Primal shows nothing. The first pass had
 * measured the BLOB-BACKED variant, whose `.content` is empty, watched njump
 * render a bare event, and credited the kind with a result the empty content
 * produced.
 *
 * SO THE PROTECTIVE PROPERTY IS THAT THE CIPHERTEXT IS NOT IN `.content` AT
 * ALL. Most clients print whatever `.content` they are handed, whosever kind it
 * arrived under. That is an argument for keeping a private body in a blob,
 * which is what the design already says, and it is NOT an argument for the kind
 * number below being ours. Encrypting the title does not rescue it either:
 * Highlighter prints "Untitled" above the base64 and Coracle prints
 * "undefined". The ruling to encrypt the title stands on the reason it was
 * given, that a plaintext title leaks what the page is about, rather than on a
 * benefit it turned out not to have.
 * `arbiter/cloistr/research/page-rendering-and-private-prior-art.md`.
 *
 * THERE IS NO 30023 MIRROR. The earlier plan was to duplicate a page's markdown
 * into a second, optional 30023 alongside our own canonical event. That is two
 * events holding the same text that can drift apart, with a canonical-versus-
 * mirror distinction no reader would understand. It only ever existed to rescue
 * a storage choice that should not have been made, and it died with that
 * choice.
 *
 * OUR OWN KIND WAS REDUCED, FOR ONE DAY, to what 30023 genuinely cannot carry:
 * a body that is not markdown (raw HTML) and a body that is ciphertext (a
 * private page). THE REVERSAL OF 2026-09-16 WIDENED IT BACK to every page we
 * publish, for the reason at the top of this file.
 *
 * BECAUSE AN `naddr` CARRIES ITS OWN KIND, A MIXED FLEET COSTS THE READER
 * NOTHING. Written to defend splitting our OWN pages across two kinds, which we
 * no longer do. It survives as the reason reading two kinds is free: a
 * stranger's long-form article opens beside one of our pages with no guessing,
 * because the kind is in the address.
 *
 * WHY THESE NUMBERS, for the two that are ours. Both must be addressable
 * (parameterized replaceable, 30000-39999) -- the page so its URL survives
 * every edit, the theme so replacing it restyles every page pointing at it.
 * Verified the relay classifies the range: cloistr-relay
 * internal/metrics/metrics.go:248-249.
 *
 * Checked against the NIP registry kind table (nostr-protocol/nips README) and
 * the nostrbook community index on 2026-09-15. The largest unassigned gap in
 * the addressable range runs 32268-34234, between "Software Application"
 * (32267) and "Addressable Video Event" (34235). 33301/33302 sit near its
 * centre -- roughly a thousand clear on either side -- so neither is adjacent
 * to a live standard, and they are consecutive so the pair reads as one family.
 * Round and memorable numbers (33000, 33333) are the ones squatters reach for
 * first, which is the reason for avoiding them rather than a stylistic one.
 *
 * STILL PROVISIONAL. No NIP registry entry is open, because opening one for a
 * number we might not keep is worse than opening none. Nothing outside this
 * file hardcodes them.
 *
 * THE RELAY'S CEILING, AND WHAT IT BINDS NOW THAT NO BODY IS INLINE. It accepts a
 * websocket message of at most 512,000 bytes and CLOSES THE CONNECTION above
 * that, with no OK and no NOTICE -- measured 2026-09-15, not reasoned about.
 * It is khatru's default, inherited rather than chosen. When it was found, the
 * relay advertised no limit at all and bisection against a closing socket was
 * the only way to learn it. That is fixed: `relay.cloistr.xyz` (software 0.6.0)
 * now serves `limitation.max_message_length: 512000`, re-checked here on
 * 2026-09-15, so the pre-check reads a real number rather than assuming one.
 * The socket still closes silently above it, which is why the pre-check is a
 * requirement and not a nicety.
 *
 * IT NO LONGER CAPS THE PAGE BODY AT ALL. It did while bodies were inline.
 * Since 2026-09-16 the body is a blob and the event carries a fixed-length hash
 * of it, so what the ceiling binds is the event's own INLINE FIELDS -- and the
 * summary is the one that can grow without bound, because it rides in the event
 * twice, once as a tag and once as the plaintext abstract NIP-50 relay search
 * reads. It binds the PUBLISHER, not this reader. Nothing in the reading path
 * enforces it and nothing should. The publish path does, against the limit the target relay
 * advertises and before the signer is asked, so an author hears about it rather
 * than watching the socket close.
 */

/**
 * A page. The event carries a title, the blob's hash and server hints; the body
 * is always a blob.
 *
 * EVERY PAGE, since 2026-09-16: markdown, raw HTML, public and private alike.
 * What follows was written while public markdown was a 30023 and this kind
 * covered only what that could not express. The distinction it draws between
 * "blob-backed" and "private" is the part that still matters, and is why it is
 * kept whole.
 *
 * "BLOB-BACKED", NOT "PRIVATE" -- settled 2026-09-15 by cloistr-orchestrator,
 * closing the question this comment used to leave open. An earlier wording
 * re-scoped the kind from "page index" to "private page", which left a PUBLIC
 * raw HTML page with no kind at all: it is blob-backed and it is not private.
 *
 * ENCRYPTION IS A PROPERTY THE EVENT CARRIES, NOT A PROPERTY OF THE KIND. A
 * public raw HTML page is a plaintext 33301 pointing at a plaintext blob. A
 * private page is a 33301 whose event is encrypted apart from its `d` tag,
 * which has to stay plaintext because it is part of the address.
 *
 * THE DISCRIMINATOR IS THE `x` TAG, and there is no `private` flag. A plaintext
 * `x` present means public blob-backed: fetch, verify, render. Absent means the
 * tags are ciphertext. A boolean flag would be decoration, for the same reason
 * a list in front of an endpoint that serves ciphertext would be.
 *
 * WHY THIS BEAT A SEPARATE KIND FOR PUBLIC HTML: a public raw HTML page flipped
 * to private KEEPS ITS ADDRESS, so every link people hold keeps working. Under
 * a split, every flip would break them. That narrowed the cost the operator
 * accepted -- a flip changing the address -- to public MARKDOWN pages, and the
 * reversal of 2026-09-16 removed even that: every page is this kind, so no flip
 * changes an address, and the cost is gone rather than narrowed.
 *
 * The parser accepted a plaintext 33301 before this was settled, so nothing
 * shipped had to change. What the ruling settles is what a PUBLISHER writes,
 * which a reader's tolerance could never have settled on its own.
 */
export const PAGE_INDEX_KIND = 33301;

/** A theme: design-token overrides shared by many pages. Read by `loadTheme`. */
export const PAGE_THEME_KIND = 33302;

/**
 * A collection: an ordered index of pages that together are a site.
 *
 * OURS, AND THAT WAS REVERSED ON 2026-09-16 FROM A COMMUNITY SPEC'S KIND. The
 * collection was going to be an NKBIP-01 Publication Index (30040), and the
 * argument for it was interoperability: a real spec, other readers, no new
 * number to justify.
 *
 * TWO THINGS KILLED IT AND THE SECOND IS THE ONE THAT GENERALISES.
 *
 * First, the sanction was never there. NKBIP-01 says members SHOULD be 30041 or
 * a nested 30040, and MAY be 30817, 30818 or 30023. Our page kind is on none of
 * those lists. A 30040 pointing at our pages was not a supported use of that
 * spec; it was a use the spec had not considered.
 *
 * Second, and this is the same failure one level up from the one already
 * recorded in this file: the interop being bought had become the BLANK ARTICLE.
 * A reader that understands 30040 would fetch our index, resolve every member to
 * a kind it cannot render, and show an empty publication. This file already
 * rejects that shape for a single page, in the words "blank in someone else's
 * client is worse than absent". Conforming an index so that strangers can render
 * nothing through it is the appearance of interoperability rather than the
 * thing.
 *
 * WHAT THE REVERSAL PAID FOR, and it is not a consolation prize. NKBIP-01
 * requires `content` to be EMPTY, and that single constraint was the only reason
 * a mixed public-and-private site needed a SECOND event: an encrypted overlay,
 * an anchoring rule to slot private entries back into position, and a fallback
 * for an anchor that no longer exists. Under our own kind the private half is
 * simply this event's own encrypted content. The second event and its merge rule
 * are deleted rather than implemented.
 *
 * WHAT WE KEPT, because it is good design and costs nothing: ordered `a` tags
 * carrying `kind:pubkey:d` coordinates with a relay hint, nested indices for
 * sections, and the `type` tag. Copying a shape is free; inheriting a constraint
 * that no longer buys anything is not. An `a` tag can still name a long-form
 * event, so indexing somebody else's article still works.
 *
 * PROVISIONAL, like the two above, and chosen to sit beside them so the family
 * reads as one.
 */
export const PAGE_COLLECTION_KIND = 33304;

/**
 * NIP-23 long-form, markdown in `.content`. SOMEBODY ELSE'S ARTICLE, or an
 * author's own written in another client. We read these. We do not write them.
 */
export const NIP23_LONG_FORM_KIND = 30023;

/**
 * The kinds an address in our URL may name, and the kinds a `nostr:` link in
 * page content may point at to become a local link.
 *
 * An `naddr` for anything else -- a wiki article, a calendar event, a software
 * application -- is a real reference to something real that we cannot display.
 * It is not routed here.
 */
export const RENDERABLE_PAGE_KINDS: readonly number[] = [
  NIP23_LONG_FORM_KIND,
  PAGE_INDEX_KIND,
];

/**
 * A share: one key, wrapped to one recipient.
 *
 * NOT OURS AND DELIBERATELY NOT NEW. The file service already publishes shares
 * on this kind, and a second kind meaning the same thing would mean a recipient
 * has to know which app sent them something before they can look for it. The
 * whole value of a share arriving on the relay is that the recipient asks ONE
 * question -- what has been shared with me -- and the answer is complete.
 *
 * WHAT THE PLAINTEXT TAGS MAY CARRY is the constraint that shapes the event: a
 * `d` for the share's own name and a `p` for the recipient, and nothing else.
 * NOT the address of the thing being shared. An address names an author, a kind
 * and an identifier, so the list of addresses in somebody's shares is the shape
 * of what they have been given. That is the same rule that keeps a private
 * collection member out of a tag, applied one level up, and it matters more
 * here because a share event is addressed to a person and therefore queryable
 * BY that person's key.
 *
 * So a stranger reading every share event on the relay learns that two keys
 * exchanged something, and nothing about what.
 */
export const SHARE_KIND = 30080;

/** BUD-03: an author's declared list of Blossom servers. */
export const BLOSSOM_SERVER_LIST_KIND = 10063;
